import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Modal } from './Modal';
import type { Product } from '../mockKnowledge';

interface AddProductModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with a fully-formed Product on submit — parent decides whether to append or replace. */
  onSave: (product: Product) => void;
  /**
   * When provided, the modal opens in edit mode: fields prefill from this
   * product and the existing id/source are preserved on save. `AddProductModal`
   * doubles as the edit form (rather than a separate EditProductModal) since
   * the two flows share every field — only the submit-time id/source handling
   * and copy differ.
   */
  initialProduct?: Product | null;
}

const emptyForm = {
  title: '',
  price: '',
  description: '',
  stock: '',
  imageUrl: '',
  category: '',
  tags: '',
};

export function AddProductModal({ open, onClose, onSave, initialProduct }: AddProductModalProps) {
  const isEditing = Boolean(initialProduct);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;
    if (initialProduct) {
      setForm({
        title: initialProduct.title,
        price: String(initialProduct.price),
        description: initialProduct.description,
        stock: String(initialProduct.stock),
        imageUrl: initialProduct.imageUrl,
        category: initialProduct.category,
        tags: initialProduct.tags.join(', '),
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, initialProduct]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!form.title.trim()) return;

    const product: Product = {
      id: initialProduct?.id ?? `manual-${Date.now()}`,
      title: form.title.trim(),
      price: Number(form.price) || 0,
      description: form.description.trim(),
      stock: Number(form.stock) || 0,
      imageUrl: form.imageUrl.trim() || '/mock/placeholder.jpg',
      category: form.category.trim() || 'Uncategorized',
      tags: form.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
      source: initialProduct?.source ?? 'manual',
      inKnowledgeBase: initialProduct?.inKnowledgeBase ?? true,
      lastSynced: initialProduct?.lastSynced,
    };

    onSave(product);
    onClose();
    toast.success(isEditing ? 'Product updated' : 'Product added to your catalog');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit product' : 'Add product manually'}
      description="Fields appear in what the AI can reference when answering customers."
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <Label htmlFor="product-title">Title</Label>
          <Input
            id="product-title"
            value={form.title}
            onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
            placeholder="e.g. Red Embroidered Kurti"
            required
            className="mt-1"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="product-price">Price (৳)</Label>
            <Input
              id="product-price"
              type="number"
              min="0"
              value={form.price}
              onChange={(event) => setForm((prev) => ({ ...prev, price: event.target.value }))}
              placeholder="1450"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="product-stock">Stock</Label>
            <Input
              id="product-stock"
              type="number"
              min="0"
              value={form.stock}
              onChange={(event) => setForm((prev) => ({ ...prev, stock: event.target.value }))}
              placeholder="24"
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="product-description">Description</Label>
          <Textarea
            id="product-description"
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            placeholder="Details the AI can quote to customers (fabric, sizing, care instructions…)"
            className="mt-1"
            rows={3}
          />
        </div>

        <div>
          <Label htmlFor="product-image">Image URL</Label>
          <Input
            id="product-image"
            value={form.imageUrl}
            onChange={(event) => setForm((prev) => ({ ...prev, imageUrl: event.target.value }))}
            placeholder="https://…"
            className="mt-1"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="product-category">Category</Label>
            <Input
              id="product-category"
              value={form.category}
              onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}
              placeholder="Women's Fashion"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="product-tags">Tags (comma separated)</Label>
            <Input
              id="product-tags"
              value={form.tags}
              onChange={(event) => setForm((prev) => ({ ...prev, tags: event.target.value }))}
              placeholder="kurti, cotton"
              className="mt-1"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">{isEditing ? 'Save changes' : 'Add product'}</Button>
        </div>
      </form>
    </Modal>
  );
}

export default AddProductModal;
